import ApexCharts from "apexcharts";
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap-daterangepicker/daterangepicker.css";
import CollapseHeader from "../../../../core/common/collapse-header";
import { API_BASE_URL } from "../../../../environment";
import { AuthContext } from "../../../../context/authcontext";
import {
  safeCreateChart,
  safeDestroyChart,
  debounce,
} from "../../../../utils/chartUtils";
import StatsTable from "./StatsTable";
import RecentActivities from "./RecentActivities";
import RowCards from "./rowCards";
import UserTasks from "./UserTasks";
import TenantSubTrend from "./super-admin/tenant-sub-trend";
import PieChart from "./super-admin/pie-chart";
import SubscriptionStatsCards from "./super-admin/subscription-stats-cards";
import { getLifecycleTypeName } from "../../../../utils/lifecycleUtils";
import "./dashboard.css";

const DealsDashboard = () => {
  const { auth, selectedTenantId, setSelectedTenantId } =
    useContext(AuthContext);
  const selectedTenant = auth?.tenants?.find(
    (t: any) => String(t.tenant_id) === String(selectedTenantId)
  );

  const isSuperAdmin =
    auth?.tenants?.some(
      (t: any) =>
        t.role_key?.toLowerCase() === "superadmin" ||
        t.role_key?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "superadmin" ||
        t.role_name?.toLowerCase() === "super_admin" ||
        t.role_key?.toLowerCase() === "platform_analyst" ||
        t.role_name?.toLowerCase() === "platformanalyst" ||
        t.role_key?.toLowerCase() === "apiadmin" ||
        t.role_key?.toLowerCase() === "api_admin" ||
        t.role_name?.toLowerCase() === "apiadmin" ||
        t.role_name?.toLowerCase() === "api_admin"
    ) || false;

  let userRole = selectedTenant?.role_key || selectedTenant?.role_name || "";
  if (isSuperAdmin && !selectedTenantId) {
    const superAdminTenant = auth?.tenants?.find(
      (t: any) =>
        t.role_key?.toLowerCase() === "superadmin" ||
        t.role_key?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "superadmin" ||
        t.role_name?.toLowerCase() === "super_admin" ||
        t.role_key?.toLowerCase() === "platform_analyst" ||
        t.role_name?.toLowerCase() === "platformanalyst" ||
        t.role_key?.toLowerCase() === "apiadmin" ||
        t.role_key?.toLowerCase() === "api_admin" ||
        t.role_name?.toLowerCase() === "apiadmin" ||
        t.role_name?.toLowerCase() === "api_admin"
    );
    userRole = superAdminTenant?.role_key || superAdminTenant?.role_name || "";
  }
  const isClientAdmin = userRole === "CLIENTADMIN";
  const isSalesUser = userRole === "SALESUSER";
  const isTeamManager = localStorage.getItem("isTeamManager") === "true";
  const userId = auth?.user_id || auth?.id || "";
  const [isDashboardReady, setIsDashboardReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cardCounts, setCardCounts] = useState({
    companies: 0,
    contacts: 0,
    leads: 0,
    pipelines: 0,
    deals: 0,
  });
  const [cardCountsLoading, setCardCountsLoading] = useState(false);
  const [animatedCounts, setAnimatedCounts] = useState({
    companies: 0,
    contacts: 0,
    leads: 0,
    pipelines: 0,
    deals: 0,
  });

  useEffect(() => {
    if (!isSuperAdmin) return;
    const activeAnimations = new Set<string>();

    Object.keys(cardCounts).forEach((key) => {
      const targetValue = cardCounts[key as keyof typeof cardCounts];
      const currentValue = animatedCounts[key as keyof typeof animatedCounts];
      if (targetValue === currentValue || activeAnimations.has(key)) return;

      activeAnimations.add(key);
      let start = currentValue;
      const duration = 1000;
      const increment = (targetValue - start) / (duration / 16);
      const animationId = Date.now() + Math.random();

      const animate = () => {
        if (!activeAnimations.has(key)) return;

        start += increment;
        if (
          (increment > 0 && start >= targetValue) ||
          (increment < 0 && start <= targetValue)
        ) {
          setAnimatedCounts((prev) => ({ ...prev, [key]: targetValue }));
          activeAnimations.delete(key);
        } else {
          setAnimatedCounts((prev) => ({ ...prev, [key]: Math.round(start) }));
          if (activeAnimations.has(key)) {
            requestAnimationFrame(animate);
          }
        }
      };

      if (targetValue !== 0) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedCounts((prev) => ({ ...prev, [key]: 0 }));
        activeAnimations.delete(key);
      }
    });
    return () => {
      activeAnimations.clear();
    };
  }, [cardCounts, isSuperAdmin]);

  const [stageBarData, setStageBarData] = useState<{
    deal: Record<string, number>;
    lead: Record<string, number>;
    pipeline: Record<string, number>;
  }>({ deal: {}, lead: {}, pipeline: {} });
  const [stageBarLoading, setStageBarLoading] = useState(false);
  const [stageMonths, setStageMonths] = useState<number>(12);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedFetch = () => {
      timeoutId = setTimeout(async () => {
        setIsLoading(true);
        setStageBarLoading(true);
        const tenantId =
          localStorage.getItem("selectedTenantId") ||
          localStorage.getItem("tenant_id") ||
          "";
        const authHeader = localStorage.getItem("authCred") || "";

        let url = tenantId
          ? `${API_BASE_URL}/dashboard/lifecycle-stage-type/?tenant_id=${tenantId}&last=${stageMonths}`
          : `${API_BASE_URL}/dashboard/lifecycle-stage-type/?last=${stageMonths}`;
        if (isSalesUser && userId) {
          url += `&sales_user_id=${userId}`;
        }

        try {
          const res = await fetch(url, {
            headers: { Authorization: `Basic ${authHeader}` },
          });
          const data = await res.json();
          setStageBarData({
            deal: data.deal || {},
            lead: data.lead || {},
            pipeline: data.pipeline || {},
          });
        } catch (err) {
          setStageBarData({ deal: {}, lead: {}, pipeline: {} });
        }
        setStageBarLoading(false);
        setIsLoading(false);
      }, 500);
    };

    debouncedFetch();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedTenantId, stageMonths, isSalesUser, userId]);

  const getStageBarChartData = (type: "deal" | "lead" | "pipeline") => {
    const obj = stageBarData[type] || {};
    const categories = Object.keys(obj);
    const values = categories.map((k) => obj[k]);
    return { categories, values };
  };

  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<ApexCharts | null>(null);

  const createLeadsChart = useCallback(
    debounce(async () => {
      const { categories, values } = getStageBarChartData("lead");
      if (categories.length === 0) return;

      const maxValue =
        values.length > 0 ? Math.ceil(Math.max(...values) * 1.2) : 10;
      const options = {
        series: [
          {
            name: "Leads",
            data: categories.map((cat, i) => ({
              x: cat,
              y: Math.round(values[i]),
            })),
          },
        ],
        chart: {
          type: "bar",
          height: 250,
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ["#00918E"],
        xaxis: {
          type: "category",
          categories,
          min: 0,
          max: maxValue,
          tickAmount: 5,
          labels: {
            formatter: (val: number) => Math.round(val),
          },
        },
      };

      await safeCreateChart(
        chartRef,
        chartInstanceRef,
        options,
        "leads-stage-chart"
      );
    }, 150),
    [stageBarData]
  );

  useEffect(() => {
    createLeadsChart();

    return () => {
      safeDestroyChart(chartInstanceRef, "leads-stage-chart");
    };
  }, [createLeadsChart]);

  const LeadsBySatge = useRef<HTMLDivElement | null>(null);
  const leadsChartInstanceRef = useRef<ApexCharts | null>(null);
  useEffect(() => {
    if (!LeadsBySatge.current) return;

    if (leadsChartInstanceRef.current) {
      try {
        leadsChartInstanceRef.current.destroy();
      } catch (error) {
        console.warn("Error destroying leads chart:", error);
      }
      leadsChartInstanceRef.current = null;
    }

    const { categories, values } = getStageBarChartData("pipeline");
    if (categories.length === 0) return;

    if (!LeadsBySatge.current) return;

    const maxValue =
      values.length > 0 ? Math.ceil(Math.max(...values) * 1.2) : 10;
    const options = {
      series: [
        {
          name: "Pipelines",
          data: categories.map((cat, i) => ({
            x: cat,
            y: Math.round(values[i]),
          })),
        },
      ],
      chart: {
        type: "bar",
        height: 250,
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["#FC0027"],
      xaxis: {
        type: "category",
        categories,
        min: 0,
        max: maxValue,
        tickAmount: 5,
        labels: {
          formatter: (val: number) => Math.round(val),
        },
      },
    };

    try {
      leadsChartInstanceRef.current = new ApexCharts(
        LeadsBySatge.current,
        options
      );
      leadsChartInstanceRef.current.render();
    } catch (error) {
      console.warn("Error creating leads chart:", error);
    }

    return () => {
      if (leadsChartInstanceRef.current) {
        try {
          leadsChartInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying leads chart in cleanup:", error);
        }
        leadsChartInstanceRef.current = null;
      }
    };
  }, [stageBarData, selectedTenantId]);

  const wonChat = useRef<HTMLDivElement | null>(null);
  const pipelineChartInstanceRef = useRef<ApexCharts | null>(null);
  useEffect(() => {
    if (!wonChat.current) return;

    if (pipelineChartInstanceRef.current) {
      try {
        pipelineChartInstanceRef.current.destroy();
      } catch (error) {
        console.warn("Error destroying pipeline chart:", error);
      }
      pipelineChartInstanceRef.current = null;
    }

    const { categories, values } = getStageBarChartData("deal");
    if (categories.length === 0) return;

    if (!wonChat.current) return;

    const maxValue =
      values.length > 0 ? Math.ceil(Math.max(...values) * 1.2) : 10;
    const options = {
      series: [
        {
          name: "Deals",
          data: categories.map((cat, i) => ({
            x: cat,
            y: Math.round(values[i]),
          })),
        },
      ],
      chart: {
        type: "bar",
        height: 250,
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["#5CB85C"],
      xaxis: {
        type: "category",
        categories,
        min: 0,
        max: maxValue,
        tickAmount: 5,
        labels: {
          style: {
            fontSize: "13px",
          },
          formatter: (val: number) => Math.round(val),
        },
      },
    };

    try {
      pipelineChartInstanceRef.current = new ApexCharts(
        wonChat.current,
        options
      );
      pipelineChartInstanceRef.current.render();
    } catch (error) {
      console.warn("Error creating pipeline chart:", error);
    }

    return () => {
      if (pipelineChartInstanceRef.current) {
        try {
          pipelineChartInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying pipeline chart in cleanup:", error);
        }
        pipelineChartInstanceRef.current = null;
      }
    };
  }, [stageBarData, selectedTenantId]);

  const dealsByYearRef = useRef<HTMLDivElement | null>(null);
  const dealsByYearChartRef = useRef<ApexCharts | null>(null);
  const [dealsByYearData, setDealsByYearData] = useState<DealsByYearData>({
    categories: [],
    deal: [],
    lead: [],
    pipeline: [],
  });
  const [dealsByYearLoading, setDealsByYearLoading] = useState(false);
  const [dealsByYearMonths, setDealsByYearMonths] = useState<number>(12);
  const [lifecycleTypeNames, setLifecycleTypeNames] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("lifecycleTypeNames") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const updated = JSON.parse(
          localStorage.getItem("lifecycleTypeNames") || "{}"
        );
        setLifecycleTypeNames((prev: Record<string, string>) => {
          if (JSON.stringify(prev) !== JSON.stringify(updated)) {
            return updated;
          }
          return prev;
        });
      } catch {
        setLifecycleTypeNames({});
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(() => {
      handleStorageChange();
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  function getLastNMonthsData(apiData: any, n: number): DealsByYearData {
    const monthNames = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    let all: Array<{
      year: number;
      month: string;
      monthIdx: number;
      type: string;
      value: number;
    }> = [];
    Object.entries(apiData).forEach(([year, obj]) => {
      if (typeof obj !== "object" || obj === null) return;
      ["deal", "lead", "pipeline"].forEach((type) => {
        const typeObj = (obj as any)[type] || {};
        Object.entries(typeObj).forEach(([month, value]) => {
          all.push({
            year: Number(year),
            month,
            monthIdx: monthNames.indexOf(month.toLowerCase()),
            type,
            value: Number(value),
          });
        });
      });
    });

    let grouped: Record<string, GroupedMonth> = {};
    all.forEach(({ year, month, monthIdx, type, value }) => {
      const key = `${year}-${monthIdx}`;
      if (!grouped[key])
        grouped[key] = { year, month, monthIdx, deal: 0, lead: 0, pipeline: 0 };
      (grouped[key] as any)[type] = value;
    });

    let arr: GroupedMonth[] = Object.values(grouped).sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.monthIdx - b.monthIdx
    );
    arr = arr.slice(-n);
    const monthShortNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return {
      categories: arr.map((x) => `${monthShortNames[x.monthIdx]} ${x.year}`),
      deal: arr.map((x) => x.deal),
      lead: arr.map((x) => x.lead),
      pipeline: arr.map((x) => x.pipeline),
    };
  }

  useEffect(() => {
    async function fetchDealsByYearForSelectedMonths() {
      setIsLoading(true);
      setDealsByYearLoading(true);

      try {
        const tenantId =
          localStorage.getItem("selectedTenantId") ||
          localStorage.getItem("tenant_id") ||
          "";
        const authHeader = localStorage.getItem("authCred") || "";

        let url = tenantId
          ? `${API_BASE_URL}/dashboard/lifecycle-type-stages-by-year/?tenant_id=${tenantId}&last=${dealsByYearMonths}`
          : `${API_BASE_URL}/dashboard/lifecycle-type-stages-by-year/?last=${dealsByYearMonths}`;
        if (isSalesUser && userId) {
          url += `&sales_user_id=${userId}`;
        }
        const res = await fetch(url, {
          headers: { Authorization: `Basic ${authHeader}` },
        });
        const data = await res.json();
        const parsed = getLastNMonthsData(data, dealsByYearMonths);
        setDealsByYearData(parsed);
      } catch {
        setDealsByYearData({
          categories: [],
          deal: [],
          lead: [],
          pipeline: [],
        });
      }
      setDealsByYearLoading(false);
      setIsLoading(false);
    }
    fetchDealsByYearForSelectedMonths();
  }, [selectedTenantId, dealsByYearMonths, isSalesUser, userId]);

  useEffect(() => {
    if (dealsByYearChartRef.current) {
      try {
        dealsByYearChartRef.current.destroy();
      } catch (error) {
        console.warn("Error destroying deals by year chart:", error);
      }
      dealsByYearChartRef.current = null;
    }

    if (dealsByYearRef.current && dealsByYearData.categories.length > 0) {
      try {
        const chart = new ApexCharts(dealsByYearRef.current, {
          series: [
            {
              name: lifecycleTypeNames.LEAD || "Lead",
              data: dealsByYearData.lead,
            },
            {
              name: lifecycleTypeNames.PIPELINE || "Pipeline",
              data: dealsByYearData.pipeline,
            },
            {
              name: lifecycleTypeNames.DEAL || "Deal",
              data: dealsByYearData.deal,
            },
          ],
          chart: {
            height: 273,
            type: "line",
            zoom: { enabled: false },
            toolbar: { show: false },
          },
          colors: ["#FC2647", "#74C374", "#26A19F"],
          dataLabels: { enabled: false },
          stroke: {
            curve: "straight",
            width: 3,
          },
          xaxis: {
            categories: dealsByYearData.categories,
            labels: {
              style: {
                fontSize: "12px",
              },
            },
          },
          yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: {
              formatter: (val: number) => Math.round(val).toString(),
            },
          },
          grid: {
            borderColor: "#e7e7e7",
            row: {
              colors: ["#f3f3f3", "transparent"],
              opacity: 0.5,
            },
          },
          legend: { position: "top", horizontalAlign: "left" },
          tooltip: {
            y: {
              formatter: (val: number) => `${val}`,
            },
          },
        });
        chart.render();
        dealsByYearChartRef.current = chart;
      } catch (error) {
        console.warn("Error creating deals by year chart:", error);
      }
    }

    return () => {
      if (dealsByYearChartRef.current) {
        try {
          dealsByYearChartRef.current.destroy();
        } catch (error) {
          console.warn(
            "Error destroying deals by year chart in cleanup:",
            error
          );
        }
        dealsByYearChartRef.current = null;
      }
    };
  }, [
    dealsByYearData,
    dealsByYearMonths,
    selectedTenantId,
    lifecycleTypeNames,
  ]);

  const location = useLocation();
  // Store just the lifecycle type key (LEAD, PIPELINE, DEAL) instead of the full string
  const [selectedLifecycleType, setSelectedLifecycleType] =
    useState<string>("LEAD");

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "selectedTenantId" || event.key === "tenant_id") {
        const newTenantId =
          localStorage.getItem("selectedTenantId") ||
          localStorage.getItem("tenant_id") ||
          "";
        setSelectedTenantId(newTenantId || null);
      }
    };
    window.addEventListener("storage", handleStorage);

    let lastTenantId = selectedTenantId;
    const interval = setInterval(() => {
      const currentTenantId =
        localStorage.getItem("selectedTenantId") ||
        localStorage.getItem("tenant_id") ||
        "";

      if (isSuperAdmin && !selectedTenantId && !currentTenantId) {
        return;
      }

      if (currentTenantId !== lastTenantId) {
        setSelectedTenantId(currentTenantId || null);
        lastTenantId = currentTenantId;
      }
    }, 1000);
    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, [selectedTenantId, isSuperAdmin]);

  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const tableContainerStyle = {
    overflowY: "auto",
  };

  const recentOptions = useMemo(
    () => [
      {
        key: "LEAD",
        label: `Recently Created ${lifecycleTypeNames.LEAD || "Lead"}`,
      },
      {
        key: "PIPELINE",
        label: `Recently Created ${lifecycleTypeNames.PIPELINE || "Pipeline"}`,
      },
      {
        key: "DEAL",
        label: `Recently Created ${lifecycleTypeNames.DEAL || "Deal"}`,
      },
    ],
    [lifecycleTypeNames]
  );

  const fetchTableData = async (
    lifecycleTypeKey: string,
    tenantIdOverride?: string
  ) => {
    setIsLoading(true);
    setLoading(true);
    setTableData([]);

    try {
      const tenantId =
        tenantIdOverride ||
        localStorage.getItem("selectedTenantId") ||
        localStorage.getItem("tenant_id") ||
        "";
      const authHeader = localStorage.getItem("authCred") || "";
      let endpoint = "";

      if (lifecycleTypeKey === "DEAL")
        endpoint = tenantId
          ? `/deal/?tenant_id=${tenantId}&order_by=desc&limit=5&is_archive=false`
          : `/deal/?order_by=desc&limit=5&is_archive=false`;
      else if (lifecycleTypeKey === "PIPELINE")
        endpoint = tenantId
          ? `/pipeline/?tenant_id=${tenantId}&order_by=desc&limit=5&is_archive=false`
          : `/pipeline/?order_by=desc&limit=5&is_archive=false`;
      else
        endpoint = tenantId
          ? `/lead/?tenant_id=${tenantId}&order_by=desc&limit=5&is_archive=false`
          : `/lead/?order_by=desc&limit=5&is_archive=false`;

      if (isSalesUser && userId) {
        endpoint += `&sales_user_id=${userId}`;
      }

      const res = await fetch(API_BASE_URL + endpoint, {
        headers: { Authorization: `Basic ${authHeader}` },
      });
      const data = await res.json();

      if (data && Array.isArray(data.results)) {
        setTableData(data.results);
      } else if (Array.isArray(data)) {
        setTableData(data);
      } else {
        setTableData([]);
      }
    } catch (e) {
      setTableData([]);
    }
    setLoading(false);
    setIsLoading(false);
  };

  const fetchCardCounts = async (tenantIdOverride?: string) => {
    if (!isSuperAdmin) return;
    setIsLoading(true);
    setCardCountsLoading(true);
    try {
      const tenantId = tenantIdOverride || selectedTenantId;
      const authHeader = localStorage.getItem("authCred") || "";

      const endpoints = [
        {
          key: "companies",
          url: tenantId
            ? `/company/?tenant_id=${tenantId}&limit=1`
            : `/company/?limit=1`,
        },
        {
          key: "contacts",
          url: tenantId
            ? `/contact/?tenant_id=${tenantId}&limit=1`
            : `/contact/?limit=1`,
        },
        {
          key: "leads",
          url: tenantId
            ? `/lead/?tenant_id=${tenantId}&limit=1&is_archive=false`
            : `/lead/?limit=1&is_archive=false`,
        },
        {
          key: "pipelines",
          url: tenantId
            ? `/pipeline/?tenant_id=${tenantId}&limit=1&is_archive=false`
            : `/pipeline/?limit=1&is_archive=false`,
        },
        {
          key: "deals",
          url: tenantId
            ? `/deal/?tenant_id=${tenantId}&limit=1&is_archive=false`
            : `/deal/?limit=1&is_archive=false`,
        },
      ];

      if (isSalesUser && userId) {
        endpoints.forEach((endpoint) => {
          endpoint.url += `&sales_user_id=${userId}`;
        });
      }

      const results = await Promise.all(
        endpoints.map(async ({ key, url }) => {
          try {
            const res = await fetch(API_BASE_URL + url, {
              headers: { Authorization: `Basic ${authHeader}` },
            });
            const data = await res.json();

            const count =
              data.count !== undefined
                ? data.count
                : data?.results?.length ??
                (Array.isArray(data) ? data.length : 0);

            return { key, count };
          } catch (e) {
            return { key, count: 0 };
          }
        })
      );

      const newCounts = results.reduce(
        (acc, { key, count }) => {
          acc[key as keyof typeof cardCounts] = count;
          return acc;
        },
        { ...cardCounts }
      );

      setCardCounts(newCounts);
    } catch (error) { }
    setCardCountsLoading(false);
    setIsLoading(false);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedFetch = () => {
      timeoutId = setTimeout(() => {
        fetchTableData(selectedLifecycleType, selectedTenantId || undefined);
      }, 300);
    };

    debouncedFetch();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedLifecycleType, selectedTenantId, isSalesUser, userId]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedFetch = () => {
      timeoutId = setTimeout(() => {
        if (isSuperAdmin) {
          fetchCardCounts(selectedTenantId || undefined);
        }
      }, 400);
    };

    debouncedFetch();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedTenantId, isSuperAdmin, isSalesUser, userId]);

  useEffect(() => {
    const hasAnyLoading = () => {
      if (isSuperAdmin) {
        return cardCountsLoading || stageBarLoading || dealsByYearLoading;
      } else {
        return loading || stageBarLoading || dealsByYearLoading;
      }
    };

    const anyApiLoading = hasAnyLoading();

    if (anyApiLoading) {
      setIsDashboardReady(false);
      setIsLoading(true);
    } else {
      const timer = setTimeout(() => {
        setIsDashboardReady(true);
        setIsLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    isSuperAdmin,
    cardCountsLoading,
    stageBarLoading,
    dealsByYearLoading,
    loading,
  ]);

  useEffect(() => {
    setIsDashboardReady(false);
    setIsLoading(true);
  }, []);

  useEffect(() => {
    return () => {
      const cleanupChart = (
        chartRef: React.MutableRefObject<ApexCharts | null>,
        name: string
      ) => {
        if (chartRef.current) {
          try {
            chartRef.current.destroy();
          } catch (error) {
            console.warn(
              `Error destroying ${name} chart during cleanup:`,
              error
            );
          }
          chartRef.current = null;
        }
      };

      cleanupChart(chartInstanceRef, "leads stage");
      cleanupChart(leadsChartInstanceRef, "pipelines stage");
      cleanupChart(pipelineChartInstanceRef, "deals stage");
      cleanupChart(dealsByYearChartRef, "deals by year");
    };
  }, [selectedTenantId]);

  const getStageBadge = (stage: string) => {
    if (!stage) return "-";
    let color = "bg-secondary";
    const s = stage.toLowerCase();
    if (s.includes("converted") || s.includes("won")) color = "bg-success";
    else if (s.includes("unqualified") || s.includes("lost"))
      color = "bg-danger";
    else if (
      s.includes("contacted") ||
      s.includes("poc") ||
      s.includes("follow")
    )
      color = "bg-primary";
    else if (s.includes("schedule")) color = "bg-info text-dark";
    else if (s.includes("pipeline")) color = "bg-warning text-dark";
    return <span className={`badge badge-pill ${color}`}>{stage}</span>;
  };

  const getEntityDisplayNameForType = (type: string) => {
    if (type.includes("Deal")) return lifecycleTypeNames.DEAL || "Deals";
    if (type.includes("Pipeline"))
      return lifecycleTypeNames.PIPELINE || "Pipelines";
    if (type.includes("Lead")) return lifecycleTypeNames.LEAD || "Leads";
    return type.replace("Recently Created ", "");
  };

  const getRecentlyCreatedLabel = () => {
    return `Recently Created ${lifecycleTypeNames[selectedLifecycleType] || selectedLifecycleType
      }`;
  };

  const getEntityCountLabel = () => {
    return `Most Recent ${lifecycleTypeNames[selectedLifecycleType] || selectedLifecycleType
      } (${tableData.length})`;
  };

  const getChartTitle = (type: string) => {
    if (type === "Deals By Stage")
      return `${lifecycleTypeNames.DEAL || "Deals"} By Stage`;
    if (type === "Leads By Stage")
      return `${lifecycleTypeNames.LEAD || "Leads"} By Stage`;
    if (type === "Pipelines By Stage" || type === "Pipelines By Stage")
      return `${lifecycleTypeNames.PIPELINE || "Pipelines"} By Stage`;
    return type;
  };

  return (
    <>
      <div className="page-wrapper dashboard-page">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center ">
                  <div className="col-md-4">
                    <h3 className="page-title d-flex align-items-center">

                      Dashboard
                    </h3>
                  </div>
                  <div className="col-md-8 float-end ms-auto">
                    <div className="d-flex title-head">
                      <div className="daterange-picker d-flex align-items-center justify-content-center">
                        <div className="head-icons mb-0">
                          <CollapseHeader />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Super Admin Dashboard */}
          {isSuperAdmin && (
            <>
              <RowCards />

              <div className="row">
                <TenantSubTrend />
                <PieChart />
              </div>

              <SubscriptionStatsCards />
            </>
          )}

          {/* Row Cards for Client Admin and Sales Users */}
          {(isClientAdmin || isSalesUser) && (
            <div>
              <RowCards />
            </div>
          )}

          {/* Regular Dashboard for Non-Super Admins */}
          {!isSuperAdmin && (
            <>
              {/* StatsTable for Client Admin and Team Manager Sales Users */}
              {(isClientAdmin || (isSalesUser && isTeamManager)) && (
                <div>
                  <div className="col-md-12">
                    <StatsTable
                      tenantId={selectedTenantId || ""}
                      isSalesUserTeamManager={isSalesUser && isTeamManager}
                    />
                  </div>
                </div>
              )}

              {/* Recently Created Lead, Pipeline, Deal */}

              <div className="row ">
                {isSuperAdmin ? (
                  cardCountsLoading ? (
                    <div className="col-md-12">
                      <div className="card">
                        <div className="card-body">
                          <div className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Loading dashboard data...
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="col-xl-3 col-sm-6 col-12 ">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="sales-info">
                                <h6>Companies</h6>
                                <h4 className="pt-2">
                                  {cardCountsLoading
                                    ? "..."
                                    : animatedCounts.companies}
                                </h4>
                              </div>
                              <div className="sales-icon">
                                <i
                                  className="ti ti-building text-warning"
                                  style={{ fontSize: "2rem" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-2 col-sm-6 col-12">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="sales-info">
                                <h6>Contacts</h6>
                                <h4 className="pt-2">
                                  {cardCountsLoading
                                    ? "..."
                                    : animatedCounts.contacts}
                                </h4>
                              </div>
                              <div className="sales-icon">
                                <i
                                  className="ti ti-users text-success"
                                  style={{ fontSize: "2rem" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-2 col-sm-6 col-12">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="sales-info">
                                <h6>{lifecycleTypeNames.LEAD || "Leads"}</h6>
                                <h4 className="pt-2">
                                  {cardCountsLoading
                                    ? "..."
                                    : animatedCounts.leads}
                                </h4>
                              </div>
                              <div className="sales-icon">
                                <i
                                  className="ti ti-chart-arcs text-info"
                                  style={{ fontSize: "2rem" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-2 col-sm-6 col-12">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="sales-info">
                                <h6>
                                  {lifecycleTypeNames.PIPELINE || "Pipelines"}
                                </h6>
                                <h4 className="pt-2">
                                  {cardCountsLoading
                                    ? "..."
                                    : animatedCounts.pipelines}
                                </h4>
                              </div>
                              <div className="sales-icon">
                                <i
                                  className="ti ti-timeline-event-exclamation text-primary"
                                  style={{ fontSize: "2rem" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between">
                              <div className="sales-info">
                                <h6>{lifecycleTypeNames.DEAL || "Deals"}</h6>
                                <h4 className="pt-2">
                                  {cardCountsLoading
                                    ? "..."
                                    : animatedCounts.deals}
                                </h4>
                              </div>
                              <div className="sales-icon">
                                <i
                                  className="ti ti-medal text-danger"
                                  style={{ fontSize: "2rem" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                ) : (
                  <div className="col-md-12 d-flex">
                    <div className="card flex-fill">
                      <div className="card-header">
                        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                          <h4 className="mb-0 d-flex align-items-center" style={{ fontSize: "1.25rem" }}>
                            <span className="db-chart-icon">
                              <i className="ti ti-list-details" />
                            </span>
                            {getEntityCountLabel()}
                          </h4>
                          <div className="dropdown">
                            <Link
                              className="dropdown-toggle px-2 py-2"
                              data-bs-toggle="dropdown"
                              to="#"
                              style={{ minWidth: 200 }}
                            >
                              <span>{getRecentlyCreatedLabel()}</span>
                            </Link>
                            <div
                              className="dropdown-menu"
                              style={{ fontSize: "0.4rem", minWidth: 200 }}
                            >
                              {recentOptions.map((option) => (
                                <button
                                  key={option.key}
                                  className={`dropdown-item${selectedLifecycleType === option.key
                                    ? " active text-white"
                                    : " text-dark"
                                    }`}
                                  style={{
                                    fontSize: "0.75rem",
                                    padding: "0.75rem 0.35rem",
                                  }}
                                  onClick={() =>
                                    setSelectedLifecycleType(option.key)
                                  }
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      {loading ? (
                        <div className="card-body">
                          <div className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Loading table data...
                              </small>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="card-body p-0"
                          style={{ borderRadius: "0 0 13px 13px", overflow: "hidden" }}
                        >
                          <div
                            className="table-responsive"
                            style={tableContainerStyle as React.CSSProperties}
                          >
                            <table
                              className="table dataTable db-table mb-0"
                              id="deals-project"
                            >
                              <thead>
                                <tr>
                                  {selectedLifecycleType === "DEAL" && (
                                    <>
                                      <th>Title</th>
                                      <th>Company</th>
                                      <th>Assigned User</th>
                                      <th>Contact Number</th>
                                      <th>
                                        Related {getLifecycleTypeName("LEAD")}
                                      </th>
                                      <th>
                                        Related{" "}
                                        {getLifecycleTypeName("PIPELINE")}
                                      </th>
                                      <th>Estimated Value</th>
                                      <th>Status</th>
                                      <th>Priority</th>
                                    </>
                                  )}
                                  {selectedLifecycleType === "PIPELINE" && (
                                    <>
                                      <th>Title</th>
                                      <th>Company</th>
                                      <th>Assigned User</th>
                                      <th>Contact Number</th>
                                      <th>
                                        Related {getLifecycleTypeName("LEAD")}
                                      </th>
                                      <th>Status</th>
                                      <th>Priority</th>
                                    </>
                                  )}
                                  {selectedLifecycleType === "LEAD" && (
                                    <>
                                      <th>Title</th>
                                      <th>Company</th>
                                      <th>Source</th>
                                      <th>Assigned User</th>
                                      <th>Contact Number</th>
                                      <th>Estimated Value</th>
                                      <th>Status</th>
                                      <th>Priority</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {Array.isArray(tableData) &&
                                  tableData.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={
                                        selectedLifecycleType === "DEAL"
                                          ? 9
                                          : selectedLifecycleType === "PIPELINE"
                                            ? 7
                                            : 8
                                      }
                                      className="text-center"
                                      style={{ padding: "60px 20px" }}
                                    >
                                      <div className="text-center">
                                        <i
                                          className="ti ti-database-off text-muted mb-3"
                                          style={{ fontSize: "3rem" }}
                                        ></i>
                                        <h5 className="text-muted">
                                          No Data Available
                                        </h5>
                                        <p className="text-muted">
                                          No{" "}
                                          {lifecycleTypeNames[
                                            selectedLifecycleType
                                          ] || selectedLifecycleType}{" "}
                                          found
                                        </p>
                                      </div>
                                    </td>
                                  </tr>
                                ) : (
                                  Array.isArray(tableData) &&
                                  tableData.map((item: any, idx: number) => {
                                    const getPriorityBadge = (
                                      priority: string
                                    ) => {
                                      if (!priority) return "-";
                                      let color = "bg-secondary";
                                      if (priority.toLowerCase() === "high")
                                        color = "bg-danger";
                                      else if (
                                        priority.toLowerCase() === "medium"
                                      )
                                        color = "bg-warning text-dark";
                                      else if (priority.toLowerCase() === "low")
                                        color = "bg-success";
                                      return (
                                        <span
                                          className={`badge badge-pill ${color}`}
                                        >
                                          {priority}
                                        </span>
                                      );
                                    };

                                    const status =
                                      item.lifecycle_type_stage?.stage_name ||
                                      "-";
                                    if (selectedLifecycleType === "DEAL") {
                                      return (
                                        <tr key={item.deal_id || idx}>
                                          <td>
                                            <Link to={`/deals/${item.deal_id}`}>
                                              {item.title?.length > 30 ? (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip id={`tooltip-${item.deal_id || item.pipeline_id || item.lead_id || Math.random()}`}>{item.title}</Tooltip>}
                                                >
                                                  <span>{`${item.title.substring(0, 30)}...`}</span>
                                                </OverlayTrigger>
                                              ) : (
                                                item.title
                                              )}
                                            </Link>
                                          </td>
                                          <td>
                                            {item.company?.company_name || "-"}
                                          </td>
                                          <td>
                                            {item.assigned_user?.full_name ||
                                              "-"}
                                          </td>
                                          <td>
                                            {item.primary_contact
                                              ?.primary_phone ||
                                              item.primary_contact
                                                ?.mobile_phone ||
                                              "-"}
                                          </td>
                                          <td>
                                            {item.realted_lead?.lead_id ? (
                                              <Link
                                                to={`/leads/${item.realted_lead.lead_id}`}
                                              >
                                                {item.realted_lead.title || "-"}
                                              </Link>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td>
                                            {item.related_pipeline
                                              ?.pipeline_id ? (
                                              <Link
                                                to={`/pipelines/${item.related_pipeline.pipeline_id}`}
                                              >
                                                {item.related_pipeline.title ||
                                                  "-"}
                                              </Link>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td>
                                            {(item.exepected_amount ||
                                              item.expected_amount ||
                                              "-") +
                                              (item.currency_code
                                                ? ` ${item.currency_code}`
                                                : "")}
                                          </td>
                                          <td>{getStageBadge(status)}</td>
                                          <td>
                                            {getPriorityBadge(item.priority)}
                                          </td>
                                        </tr>
                                      );
                                    } else if (
                                      selectedLifecycleType === "PIPELINE"
                                    ) {
                                      return (
                                        <tr key={item.pipeline_id || idx}>
                                          <td>
                                            <Link
                                              to={`/pipelines/${item.pipeline_id}`}
                                            >
                                              {item.title?.length > 30 ? (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip id={`tooltip-${item.deal_id || item.pipeline_id || item.lead_id || Math.random()}`}>{item.title}</Tooltip>}
                                                >
                                                  <span>{`${item.title.substring(0, 30)}...`}</span>
                                                </OverlayTrigger>
                                              ) : (
                                                item.title
                                              )}
                                            </Link>
                                          </td>
                                          <td>
                                            {item.company?.company_name || "-"}
                                          </td>
                                          <td>
                                            {item.assigned_user?.full_name ||
                                              "-"}
                                          </td>
                                          <td>
                                            {item.primary_contact
                                              ?.primary_phone ||
                                              item.primary_contact
                                                ?.mobile_phone ||
                                              "-"}
                                          </td>
                                          <td>
                                            {item.realted_lead?.lead_id ? (
                                              <Link
                                                to={`/leads/${item.realted_lead.lead_id}`}
                                              >
                                                {item.realted_lead.title || "-"}
                                              </Link>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td>{getStageBadge(status)}</td>
                                          <td>
                                            {getPriorityBadge(item.priority)}
                                          </td>
                                        </tr>
                                      );
                                    } else {
                                      return (
                                        <tr key={item.lead_id || idx}>
                                          <td>
                                            <Link to={`/leads/${item.lead_id}`}>
                                              {item.title?.length > 30 ? (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={<Tooltip id={`tooltip-${item.deal_id || item.pipeline_id || item.lead_id || Math.random()}`}>{item.title}</Tooltip>}
                                                >
                                                  <span>{`${item.title.substring(0, 30)}...`}</span>
                                                </OverlayTrigger>
                                              ) : (
                                                item.title
                                              )}
                                            </Link>
                                          </td>
                                          <td>
                                            {item.company?.company_name || "-"}
                                          </td>
                                          <td>{item.source || "-"}</td>
                                          <td>
                                            {item.assigned_user?.full_name ||
                                              "-"}
                                          </td>
                                          <td>
                                            {item.primary_contact
                                              ?.primary_phone ||
                                              item.primary_contact
                                                ?.mobile_phone ||
                                              "-"}
                                          </td>
                                          <td>
                                            {(item.estimated_value
                                              ? item.estimated_value
                                              : "-") +
                                              (item.currency_code
                                                ? ` ${item.currency_code}`
                                                : "")}
                                          </td>
                                          <td>{getStageBadge(status)}</td>
                                          <td>
                                            {getPriorityBadge(item.priority)}
                                          </td>
                                        </tr>
                                      );
                                    }
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Activities/Tasks and Leads by Stages Row */}
              <div className="row">
                {/* Recent Activities for Client Admin or Tasks for Sales Users */}
                {(isClientAdmin || isSalesUser) && (
                  <div className="col-md-6">
                    {isClientAdmin ? (
                      <RecentActivities tenantId={selectedTenantId || ""} />
                    ) : (
                      <UserTasks
                        userId={userId}
                        tenantId={selectedTenantId || ""}
                      />
                    )}
                  </div>
                )}

                {/* Leads by Stages Chart */}
                <div
                  className={
                    isClientAdmin || isSalesUser
                      ? "col-md-6 d-flex"
                      : "col-md-12 d-flex"
                  }
                >
                  <div className="card flex-fill">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4 className="mb-0 d-flex align-items-center">
                          <span className="db-chart-icon">
                            <i className="ti ti-chart-bar" />
                          </span>
                          {getChartTitle(`${lifecycleTypeNames.LEAD} By Stage`)}
                        </h4>

                        <div className="dropdown">
                          <Link
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            to="#"
                          >
                            {`Last ${stageMonths} months`}
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            {[3, 6, 12].map((val) => (
                              <button
                                key={val}
                                className="dropdown-item"
                                onClick={() => setStageMonths(val)}
                              >
                                {`Last ${val} months`}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {stageBarLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 300 }}
                        >
                          <div className="text-center">
                            <div
                              className="spinner-border text-primary mb-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted">Loading chart data...</p>
                          </div>
                        </div>
                      ) : Object.keys(stageBarData.lead || {}).length === 0 ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 300 }}
                        >
                          <div className="text-center">
                            <i
                              className="ti ti-chart-bar text-muted mb-3"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <h5 className="text-muted">No Data Available</h5>
                            <p className="text-muted">
                              No {lifecycleTypeNames.LEAD || "leads"} stage data
                              found for the selected period
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          id="leads-stage-chart"
                          key={`leads-chart-${selectedTenantId}-${stageMonths}`}
                          ref={chartRef}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4 className="mb-0 d-flex align-items-center">
                          <span className="db-chart-icon">
                            <i className="ti ti-chart-histogram" />
                          </span>
                          {getChartTitle(
                            `${lifecycleTypeNames.PIPELINE} By Stage`
                          )}
                        </h4>
                        <div className="dropdown">
                          <Link
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            to="#"
                          >
                            {`Last ${stageMonths} months`}
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            {[3, 6, 12].map((val) => (
                              <button
                                key={val}
                                className="dropdown-item"
                                onClick={() => setStageMonths(val)}
                              >
                                {`Last ${val} months`}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {stageBarLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <div
                              className="spinner-border text-danger mb-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted">Loading chart data...</p>
                          </div>
                        </div>
                      ) : Object.keys(stageBarData.pipeline || {}).length ===
                        0 ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <i
                              className="ti ti-timeline-event text-muted mb-3"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <h5 className="text-muted">No Data Available</h5>
                            <p className="text-muted">
                              No {lifecycleTypeNames.PIPELINE || "pipelines"}{" "}
                              stage data found for the selected period
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          id="pipelines-stage-chart"
                          key={`pipelines-chart-${selectedTenantId}-${stageMonths}`}
                          ref={LeadsBySatge}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4 className="mb-0 d-flex align-items-center">
                          <span className="db-chart-icon">
                            <i className="ti ti-medal" />
                          </span>
                          {getChartTitle(`${lifecycleTypeNames.DEAL} By Stage`)}
                        </h4>
                        <div className="dropdown">
                          <Link
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            to="#"
                          >
                            {`Last ${stageMonths} months`}
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            {[3, 6, 12].map((val) => (
                              <button
                                key={val}
                                className="dropdown-item"
                                onClick={() => setStageMonths(val)}
                              >
                                {`Last ${val} months`}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {stageBarLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <div
                              className="spinner-border text-success mb-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted">Loading chart data...</p>
                          </div>
                        </div>
                      ) : Object.keys(stageBarData.deal || {}).length === 0 ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <i
                              className="ti ti-medal text-muted mb-3"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <h5 className="text-muted">No Data Available</h5>
                            <p className="text-muted">
                              No {lifecycleTypeNames.DEAL || "deals"} stage data
                              found for the selected period
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          id="deals-stage-chart"
                          key={`deals-chart-${selectedTenantId}-${stageMonths}`}
                          ref={wonChat}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 d-flex">
                  <div className="card w-100">
                    <div className="card-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h4 className="mb-0 d-flex align-items-center">
                          <span className="db-chart-icon">
                            <i className="ti ti-chart-line" />
                          </span>
                          {lifecycleTypeNames.DEAL || "Deals"} by Year
                        </h4>
                        <div className="d-flex align-items-center flex-wrap row-gap-2">
                          <div className="dropdown">
                            <Link
                              className="dropdown-toggle"
                              data-bs-toggle="dropdown"
                              to="#"
                            >
                              {`Last ${dealsByYearMonths} months`}
                            </Link>
                            <div className="dropdown-menu dropdown-menu-end">
                              {[12, 6, 3].map((val) => (
                                <Link
                                  to="#"
                                  className="dropdown-item"
                                  key={val}
                                  onClick={() => setDealsByYearMonths(val)}
                                >
                                  {`Last ${val} months`}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {dealsByYearLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <div
                              className="spinner-border text-primary mb-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted">Loading chart data...</p>
                          </div>
                        </div>
                      ) : dealsByYearData.categories.length === 0 ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: 250 }}
                        >
                          <div className="text-center">
                            <i
                              className="ti ti-chart-line text-muted mb-3"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <h5 className="text-muted">No Data Available</h5>
                            <p className="text-muted">
                              No chart data found for the selected period
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          id="deals-year-chart"
                          key={`deals-year-chart-${selectedTenantId}-${dealsByYearMonths}`}
                          ref={dealsByYearRef}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DealsDashboard;

interface DealsByYearData {
  categories: string[];
  deal: number[];
  lead: number[];
  pipeline: number[];
}
interface GroupedMonth {
  year: number;
  month: string;
  monthIdx: number;
  deal: number;
  lead: number;
  pipeline: number;
}
